// Puts the app-wide loader up for RTK Query requests, the way async-ops
// operations already do for the classic pages.
//
// The classic path is hideShowCommonLoader in ../common_components/mdae-framework:
// it tracks in-flight ASYNC_OPERATION names in the module-level
// `asyncActionsInProgress` registry and dispatches SHOW_LOADER / HIDE_LOADER as
// that registry fills and empties. RTK Query's own lifecycle actions
// (`riskAreasApi/executeQuery/pending` and friends) don't match that predicate,
// so before this middleware existed the global loader never appeared for any
// RTK Query request and each consumer had to hand-roll an inline indicator.
//
// This feeds the *same* registry rather than keeping its own. That is what makes
// the two mechanisms cooperate: the HIDE_LOADER reducer only actually hides once
// the registry is empty, so a saga operation finishing while an RTK Query
// request is still in flight no longer pulls the loader out from under it, and
// vice versa.
import {
  SHOW_LOADER,
  HIDE_LOADER,
} from "../common_components/mdae-common-reducer";
import {
  addAsyncActionInProgress,
  removeAsyncActionInProgress,
} from "../common_components/mdae-framework";
import { rtkQueryNonLoaderEndpoints } from "../constants/rtk-query-non-loader-endpoints";

// Registry keys are namespaced by requestId, which is unique per request. The
// classic path keys on operation name instead, so two concurrent calls to the
// same operation collapse to one key and the first completion releases it; here
// each request holds its own key.
const registryKey = (requestId: string) => `rtkq:${requestId}`;

// Backstop for a request that never settles. Every service call behind this API
// goes through a timeout helper so promises do settle in practice, but a
// full-screen blocking overlay is unrecoverable without a page reload, so the
// key is released regardless after this long.
const STUCK_REQUEST_RELEASE_MS = 60000;

interface RtkQueryApiLike {
  reducerPath: string;
}

/**
 * @param apis the RTK Query api slices whose requests should raise the loader.
 */
export const createRtkQueryLoaderMiddleware = (apis: RtkQueryApiLike[]) => {
  const reducerPaths = apis.map((api) => api.reducerPath);
  const releaseTimers: Record<string, any> = {};

  // Only RTK Query's request thunks, which are `${reducerPath}/executeQuery`
  // and `${reducerPath}/executeMutation` (infinite queries go through the query
  // thunk too). Cache-management actions like removeQueryResult carry no
  // requestId and are ignored.
  const isTrackedRequestAction = (action: any) =>
    typeof action?.type === "string" &&
    !!action?.meta?.requestId &&
    reducerPaths.some((path) => action.type.startsWith(`${path}/execute`));

  const reducerPathOf = (action: any) =>
    reducerPaths.find((path) => action.type.startsWith(`${path}/`));

  // `stateBeforeAction` matters: this runs ahead of next(action), so the cache
  // entry still holds whatever it held before this request started.
  const shouldShowLoader = (action: any, stateBeforeAction: any) => {
    const arg = action.meta?.arg ?? {};
    const reducerPath = reducerPathOf(action);

    if (rtkQueryNonLoaderEndpoints[`${reducerPath}/${arg.endpointName}`])
      return false;

    // A write is always something the user just asked for and must not be
    // interrupted, so it blocks the screen for its duration.
    if (arg.type === "mutation") return true;

    // An infinite query's fetchNextPage / fetchPreviousPage. The rows already on
    // screen stay usable while it runs, and the section renders its own
    // "Loading more…" row, so blocking everything would be wrong.
    if (arg.direction) return false;

    // The cache already has something to render for this exact arg, so this is a
    // background refetch — refetchOnMountOrArgChange, a poll, an invalidation
    // after a write. The previous data stays on screen throughout.
    const cached = stateBeforeAction?.[reducerPath]?.queries?.[arg.queryCacheKey];
    if (cached?.data !== undefined) return false;

    // A cold query: nothing to show yet, which is exactly the case the loader
    // exists for.
    return true;
  };

  const release = (key: string, store: any) => {
    if (releaseTimers[key]) {
      clearTimeout(releaseTimers[key]);
      delete releaseTimers[key];
    }
    removeAsyncActionInProgress(key);
    // The reducer's own guard decides whether this actually hides the loader —
    // it won't while any other request, saga or RTK Query, is still tracked.
    store.dispatch({ type: HIDE_LOADER });
  };

  return (store: any) => (next: any) => (action: any) => {
    if (!isTrackedRequestAction(action)) return next(action);

    const key = registryKey(action.meta.requestId);

    if (action.meta.requestStatus === "pending") {
      if (shouldShowLoader(action, store.getState())) {
        addAsyncActionInProgress(key);
        store.dispatch({ type: SHOW_LOADER });
        releaseTimers[key] = setTimeout(() => {
          console.warn(
            `[rtk-query-loader] releasing ${action.meta?.arg?.endpointName} after ${STUCK_REQUEST_RELEASE_MS}ms without a result`
          );
          release(key, store);
        }, STUCK_REQUEST_RELEASE_MS);
      }
      return next(action);
    }

    // A query skipped because an identical one was already running is rejected
    // with meta.condition, and never had a matching pending to release.
    // Everything else — fulfilled, a real failure, an aborted thunk — releases.
    if (!action.meta.condition) release(key, store);

    return next(action);
  };
};
